/**
 * Page: EditEnterprise
 * Admin-only editor for enterprise account settings. Loads data from route
 * state or API, validates input, supports optional logo upload, and submits.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import Footer from '../../../components/shared/Footer';
import { LoadingSpinner, ButtonSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { PageHeader } from '../../../components/ui/layout/page-header';
import { Card, CardContent } from '../../../components/ui/layout/card';
import { Label } from '../../../components/ui/inputs/label';
import { Textarea } from '../../../components/ui/inputs/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/inputs/select';
import { ErrorMessage } from '../../../components/ui/feedback/error-message.jsx';
import { Building2, ArrowLeft, Save } from 'lucide-react';
import ImageUploader from '../../../components/admin/createTemplates/ImageUploader';
import { isUserAdmin } from '../../../utils/admin/adminUtils';
import api from '../../../api';
import { Input } from '../../../components/ui/inputs/input';

export default function EditEnterprise() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  // Form state - Initialize with data from location state if available
  const [formData, setFormData] = useState({
    enterpriseName: '',
    enterpriseDomain: '',
    adminEmail: '',
    maxAccounts: ''
    // Removed planType, planBilling, and access control fields since they're always premium with full access
  });

  // Check if user is admin
  const isAdmin = isUserAdmin(user);

  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      } else {
        // Load enterprise data
        loadEnterpriseData();
      }
    } else if (user === null) {
      navigate('/login');
    }
  }, [navigate, user, isAdmin, id]);

  const loadEnterpriseData = async () => {
    try {
      // First try to use data passed via navigation state
      if (location.state?.enterprise) {
        const enterprise = location.state.enterprise;
        setFormData({
          enterpriseName: enterprise.enterpriseName || '',
          enterpriseDomain: enterprise.enterpriseDomain || '',
          adminEmail: enterprise.adminEmail || '',
          maxAccounts: enterprise.maxAccounts || ''
          // Removed planType, planBilling, and access control fields since they're always premium with full access
        });
        setIsLoading(false);
        return;
      }

      // If no state data, fetch from API
      const response = await api.get(`/admin/enterprises/${id}`);
      const enterprise = response.enterprise;
      
      if (enterprise) {
        setFormData({
          enterpriseName: enterprise.enterpriseName || '',
          enterpriseDomain: enterprise.enterpriseDomain || '',
          adminEmail: enterprise.adminEmail || '',
          maxAccounts: enterprise.maxAccounts || ''
          // Removed planType, planBilling, and access control fields since they're always premium with full access
        });
      } else {
        addToast('Enterprise not found', 'error');
        navigate('/app/private/manageEnterprises');
      }
    } catch (err) {
      addToast('Failed to load enterprise data', 'error');
      navigate('/app/private/manageEnterprises');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) {
      setError(null);
    }
  };

  const handleImageChange = (file) => {
    setLogoFile(file);
    if (error) {
      setError(null);
    }
  };

  const validateForm = () => {
    const { enterpriseName, enterpriseDomain } = formData;

    if (!enterpriseName.trim()) {
      return 'Enterprise name is required';
    }

    if (!enterpriseDomain.trim()) {
      return 'Enterprise domain is required';
    }

    // Validate domain format
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(enterpriseDomain.trim())) {
      return 'Please enter a valid domain (e.g., company.com)';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      
      // Add all fields
      submitData.append('enterpriseName', formData.enterpriseName);
      submitData.append('enterpriseDomain', formData.enterpriseDomain);
      submitData.append('adminEmail', formData.adminEmail);
      submitData.append('maxAccounts', formData.maxAccounts);
      // Always set to premium with full access
      submitData.append('planType', 'premium');
      submitData.append('planBilling', 'monthly');
      // Removed access control fields since they're always true on backend

      // Add logo file if provided
      if (logoFile && logoFile instanceof File) {
        submitData.append('logo', logoFile);
      }

      const response = await api.put(`/admin/enterprises/${id}`, submitData);
      
      addToast('Enterprise updated successfully', 'success');
      
      // Navigate back to manage enterprises page
      navigate('/app/private/manageEnterprises');
      
    } catch (err) {
      console.error('Error updating enterprise:', err);
      const errorMessage = err.response?.data?.error || 'Failed to update enterprise';
      setError(errorMessage);
      addToast(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading enterprise data..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto pb-20">
      {/* Back to Manage Enterprises button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/manageEnterprises')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Manage Enterprises
        </button>
      </div>

      <PageHeader
        title="Edit Enterprise"
        description="Update enterprise account settings and configuration"
      >
        <button
          onClick={() => navigate('/app/private/manageEnterprises')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
        >
          <Building2 className="h-4 w-4" />
          Manage Enterprises
        </button>
      </PageHeader>

      <Card className="mt-8">
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="enterpriseName">Enterprise Name *</Label>
                <input
                  id="enterpriseName"
                  type="text"
                  value={formData.enterpriseName}
                  onChange={(e) => handleInputChange('enterpriseName', e.target.value)}
                  placeholder="e.g., Advocacy Lab Inc."
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  required
                />
              </div>

              <div>
                <Label htmlFor="enterpriseDomain">Enterprise Domain *</Label>
                <Input
                  id="enterpriseDomain"
                  type="text"
                  value={formData.enterpriseDomain}
                  onChange={(e) => handleInputChange('enterpriseDomain', e.target.value)}
                  placeholder="company.com"
                  className="mt-1"
                  required
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Any user with an email from this domain can sign up for the enterprise plan after email verification.
                </p>
              </div>

              <div>
                <Label htmlFor="adminEmail">Admin Email</Label>
                <Input
                  id="adminEmail"
                  type="email"
                  value={formData.adminEmail}
                  onChange={(e) => handleInputChange('adminEmail', e.target.value)}
                  placeholder="admin@company.com"
                  className="mt-1"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Enter the admin email for this enterprise.
                </p>
              </div>

              <div>
                <Label htmlFor="maxAccounts">Max Accounts</Label>
                <Input
                  id="maxAccounts"
                  type="text"
                  value={formData.maxAccounts}
                  onChange={(e) => handleInputChange('maxAccounts', e.target.value)}
                  placeholder="e.g., 100"
                  className="mt-1"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Enter the maximum number of accounts allowed for this enterprise.
                </p>
              </div>

              {/* Removed Plan Type and Plan Billing fields */}
            </div>

            <div>
              <Label htmlFor="enterpriseLogo">Enterprise Logo</Label>
              <div className="mt-1">
                <ImageUploader
                  onImageChange={handleImageChange}
                  error={error && error.includes('logo') ? error : null}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Upload a new company logo image (optional - leave empty to keep current logo)
              </p>
            </div>

            {/* Enterprise Access Controls section removed - all enterprises get premium access */}

            {error && (
              <ErrorMessage message={error} />
            )}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
              >
                {isSubmitting ? (
                  <>
                    <ButtonSpinner />
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Update Enterprise
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate('/app/private/manageEnterprises')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Footer />
    </div>
  );
} 